$input-height: 48px;
$input-padding: 0 44px;
$error-text-padding: $input-padding;

:host {
    --form-search-field-min-height: var(--form-min-height);

    display: inline-block;
    min-width: 140px;
    position: relative;

    ::slotted(input) {
        @include font-p-default;

        background: var(--color-background-interaction-secondary-inverted);
        border-bottom: 1px solid var(--color-border-interaction-default);
        border-left: 0;
        border-right: 0;
        border-top: 0;
        border-radius: 0;
        box-sizing: border-box;
        color: var(--color-text-default);
        display: block;
        height: var(--form-search-field-min-height, $input-height);
        line-height: var(--form-search-field-min-height, $input-height);
        outline-offset: -1px;
        padding: var(--input-padding, $input-padding);
        transition: outline-offset 0.1s linear;
        width: 100%;
    }

    ::slotted(input:focus) {
        border-bottom: 1px solid var(--color-border-interaction-focus);
        outline: 2px solid var(--color-border-interaction-focus);
        outline-offset: 2px;
    }

    ::slotted(input:hover) {
        border-bottom: 1px solid var(--color-border-interaction-hover);
    }

    ::slotted(input:active) {
        border-bottom: 1px solid var(--color-border-interaction-focus);
        outline: 2px solid var(--color-border-interaction-focus);
    }

    ::slotted(input:disabled) {
        border-bottom: 1px solid var(--color-border-interaction-disabled);
        color: var(--color-icon-disabled);
        opacity: 0.5;
        pointer-events: none;
    }

    ::slotted(.error-text) {
        display: none;
    }

    .icon-search {
        color: var(--color-icon-default);
        left: var(--form-padding-horizontal, 8px);
        // disabling pointer events makes it possible to interact with the input
        // without being interrupted by the search icon
        pointer-events: none;
        position: absolute;
        top: var(--icon-top, 12px);
    }

    .button-reset {
        position: absolute;
        right: var(--form-padding-horizontal, 8px);
        top: var(--icon-32-top, 8px);
    }

    .datalist {
        all: unset;
        box-shadow: var(--shadow-large);
        border: 1px solid var(--color-border-interaction-default);
        display: none;
        margin-top: 4px;
        position: absolute;
        left: 0;
        top: var(--form-search-field-min-height, $input-height);
        width: 100%;
    }

    .unset {
        all: unset;
    }

    .datalist-item {
        @include font-p-default;

        background-color: var(--color-background-interaction-secondary-inverted);
        border-bottom: 1px solid var(--color-border-interaction-default);
        color: var(--color-text-default);
        cursor: pointer;
        display: block;
        padding: 12px 44px;
        position: relative;

        &:last-child {
            border-bottom: 0;
        }

        &:hover {
            @include overlay(--color-overlay-secondary-hover);
        }

        &.active,
        &:active {
            @include overlay(--color-overlay-secondary-active);
        }
    }

    #qrcode {
        color: var(--color-icon-default);
        height: 24px;
        right: var(--form-padding-horizontal, 8px);
        position: absolute;
        top: var(--icon-top, 12px);
        width: 24px;
    }

    #qr-dialog {
        display: none;
        flex-direction: column;
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 5000;
        background-color: black;
        object-fit: cover;
        margin: 0;
    }

    #qr-cancel {
        position: fixed;
        color: white;
        margin-top: 1rem;
        cursor: pointer;
        padding: 1rem;
        max-width: fit-content;
    }

    #qr-video {
        margin: auto 0;
    }
}

:host(.has-density-mode) {
    --icon-top: calc((var(--form-search-field-min-height) - 24px) / 2);
    --icon-32-top: calc((var(--form-search-field-min-height) - 32px) / 2);
    --input-padding-left: calc(var(--form-padding-horizontal) + 24px + var(--form-gap-inside));
    --input-padding: 0 var(--input-padding-left) 0 var(--input-padding-left);
}

:host([inline]) {
    --icon-top: calc((var(--form-search-field-min-height) - 20px) / 2);
    --icon-32-top: calc((var(--form-search-field-min-height) - 24px) / 2);
    --form-search-field-min-height: var(--form-search-field-min-height-small, 32px);
}

:host([error]) {
    ::slotted(input) {
        border-bottom: 1px solid var(--color-border-interaction-invalid);
    }

    ::slotted(.error-text) {
        @include font-error;

        display: block;
        margin-top: 4px;
        padding: $error-text-padding;
    }
}

:host([size='xs']:not(.has-density-mode)) {
    ::slotted(input) {
        height: 32px;
    }

    .icon-search,
    .button-reset {
        top: 6px;
    }

    .datalist {
        top: 32px;
    }
}

:host([focus]) {
    .datalist {
        display: block;
    }
}

:host(.dirty) {
    ::slotted(input) {
        @include font-p-bold;
    }
}

// website styling

:host(.sew-ws) {
    --label-color: #{$color-grey-03};
    --input-color: #{$color-grey-01-dark};
    --option-color: #{$color-grey-02-steel};
    --option-border-color: #{$color-grey-05-light};
    --option-hover-background-color: #{$color-grey-06-bright};
    --border-color: #{$color-grey-03};
    --outline-color: #{$color-grey-04-middle};
    --icon-color: #{$color-grey-02-steel};
    --background-color: #{$color-white};

    display: block;

    ::slotted(.help-text) {
        @include typo-ws-paragraph-s-fixed;

        color: var(--input-color);
        margin-top: 8px;
        padding: 0 0 0 52px;
    }

    ::slotted(input) {
        @include typo-ws-paragraph-fixed;

        color: var(--input-color);
        height: 56px;
        padding: 0 52px;
        border: 1px solid var(--border-color) !important;
        background: var(--background-color);
        z-index: 1;
    }

    ::slotted(input)::placeholder {
        color: var(--label-color);
    }

    ::slotted(input:focus) {
        border-bottom-color: inherit;
        outline-color: var(--outline-color);
    }

    .icon-search {
        color: var(--label-color);
        left: 16px;
        top: 16px;
        width: 24px;
    }

    .ws-button-reset {
        all: unset;
        position: absolute;
        right: 12px;
        top: 14px;
        color: var(--label-color);

        .svg {
            svg {
                width: 28px;
            }
        }

        &:hover {
            cursor: pointer;
        }
    }

    .datalist {
        margin-top: 0;
        top: 60px;
    }

    .datalist-item {
        @include typo-ws-paragraph-fixed;

        background-color: var(--background-color);
        border-bottom-color: var(--option-border-color);
        color: var(--option-color);
        padding: 16px 52px;

        b {
            font-weight: normal;
        }

        &:hover {
            @include typo-ws-paragraph-bold-fixed;

            color: var(--input-color);
            background-color: var(--option-hover-background-color);
        }
    }
}

:host(.sew-ws[size='L']) {
    ::slotted(.help-text) {
        padding: 0 0 0 80px;
    }

    ::slotted(input) {
        @include typo-ws-copy-xl;

        font-size: 28px;
        line-height: 36px;

        @include mq-1 {
            font-size: 20px;
            line-height: 28px;
        }

        height: 80px;
        padding: 0 80px;
    }

    .icon-search {
        left: 20px;
        top: 20px;
        width: 40px;

        svg {
            width: 100%;
        }
    }

    .ws-button-reset {
        right: 26px;
        top: 26px;

        .svg {
            width: 28px;
        }
    }

    .datalist {
        top: 85px;
    }

    .datalist-item {
        padding: 16px 80px;
    }
}

:host(.sew-ws[size='xs']) {
    ::slotted(.help-text) {
        padding: 0 44px;
    }

    ::slotted(input) {
        @include typo-ws-paragraph;

        height: 48px;
        padding: 0 44px;
    }

    .icon-search {
        left: 12px;
        top: 12px;
    }

    .ws-button-reset {
        right: 6px;
        top: 10px;

        .svg {
            width: 28px;
        }
    }

    .datalist {
        top: 48px;
    }
}

@include mq-2 {
    :host(.sew-ws[size='L']) {
        ::slotted(input) {
            height: 64px;
            padding: 0 64px;
        }

        ::slotted(.help-text) {
            padding: 0 0 0 64px;
        }

        .icon-search {
            left: 12px;
            top: 12px;
        }

        .ws-button-reset {
            right: 18px;
            top: 18px;
        }

        .datalist {
            top: 64px;
        }
    }
}

@include mq-1 {
    :host(.sew-ws[size='L']) {
        ::slotted(input) {
            height: 56px;
            padding: 0 56px;
        }

        ::slotted(.help-text) {
            padding: 0 0 0 56px;
        }

        .icon-search {
            left: 8px;
            top: 8px;
        }

        .ws-button-reset {
            right: 14px;
            top: 14px;
        }

        .datalist {
            top: 56px;
        }
    }
}

:host([no-search-icon]) {
    ::slotted(input),
    ::slotted(.help-text),
    .datalist-item {
        padding-left: 12px;
    }
}

:host(.sew-ws[no-search-icon]) {
    ::slotted(input),
    ::slotted(.help-text),
    .datalist-item {
        padding-left: 16px;
    }
}

:host(.sew-ws[no-search-icon][size='L']) {
    ::slotted(input),
    ::slotted(.help-text),
    .datalist-item {
        padding-left: 24px;
    }
}

:host(.sew-ws.sew-ws-dark) {
    --label-color: #{$color-grey-04-middle};
    --input-color: #{$color-white};
    --option-color: #{$color-grey-05-light};
    --option-border-color: #{$color-grey-03};
    --option-hover-background-color: #{$color-grey-02-steel};
    --border-color: #{$color-grey-03};
    --outline-color: #{$color-white};
    --icon-color: #{$color-grey-02-steel};
    --background-color: #{$color-grey-01-dark};
}
